import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Snackbar,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search,
  VpnKey,
  ContentCopy,
  Delete,
  CheckCircle,
  Cancel,
  Visibility,
  VisibilityOff,
  Refresh,
  Tune,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import { resolveStudioLogoUrl } from '../utils/studioLogo';

interface Studio {
  _id: string;
  studioName: string;
  email: string;
  city?: string;
  state?: string;
  images?: string[];
  isActive: boolean;
  whitelabelEnabled?: boolean;
  whitelabelAllowedOrigins?: string[];
}

interface WhitelabelConfig {
  appSource?: string;
  appName?: string;
  senderName?: string;
  replyToEmail?: string;
  supportEmail?: string;
  supportContact?: string;
  websiteUrl?: string;
  logoUrl?: string;
  primaryColor?: string;
  accentColor?: string;
  razorpayKeyId?: string;
  razorpayKeySecret?: string;
  instructorViewAllBookings?: boolean;
  privacyPolicyUrl?: string;
  termsUrl?: string;
  whatsappNumber?: string;
  deepLinkScheme?: string;
  playStoreUrl?: string;
  appStoreUrl?: string;
  linkBaseUrl?: string;
  ownerNotifPrefs?: {
    onBookingConfirmed?: boolean;
    onBookingCancelled?: boolean;
    onPackBookingConfirmed?: boolean;
    preventEventCancellation?: boolean;
    preventDropInCancellation?: boolean;
  };
}

interface PartnerStudio {
  _id: string;
  studioName: string;
  email: string;
  whitelabelEnabled: boolean;
  whitelabelAllowedOrigins: string[];
  whitelabelConfig?: WhitelabelConfig;
}

const WhitelabelPage: React.FC = () => {
  const [allStudios, setAllStudios] = useState<Studio[]>([]);
  const [partnerStudios, setPartnerStudios] = useState<PartnerStudio[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Generate key dialog
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedStudio, setSelectedStudio] = useState<Studio | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedApiKey, setGeneratedApiKey] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  // Revoke dialog
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [studioToRevoke, setStudioToRevoke] = useState<PartnerStudio | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  // Branding config dialog
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configStudio, setConfigStudio] = useState<PartnerStudio | null>(null);

  // ── IS THIS STUDIO A BRANCH, OR IS IT THE APP? ──────────────────────────
  //
  // App identity (appSource, store links, colours) describes ONE app binary.
  // Two branches of a brand cannot have different values for it, and a
  // different `appSource` on a branch would make it a separate app to every
  // allow-list, FCM route and settlement bucket in the system.
  //
  // So for a branch those fields are shown READ-ONLY and edited once on the
  // brand. For everything else — the eight single-studio apps — this is false
  // and the dialog is exactly what it is today.
  const [brandBranchCounts, setBrandBranchCounts] = useState<Record<string, number>>({});
  const brandOf = (st?: PartnerStudio | null): string | null =>
    st && (st as any).brand ? String((st as any).brand?._id ?? (st as any).brand) : null;
  const isBranchOfBrand = (st?: PartnerStudio | null): boolean => {
    const b = brandOf(st);
    return !!b && (brandBranchCounts[b] ?? 1) > 1;
  };
  const [configForm, setConfigForm] = useState<WhitelabelConfig>({});
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [showRazorpaySecret, setShowRazorpaySecret] = useState(false);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [studiosRes, partnerRes] = await Promise.all([
        apiService.getStudios(),
        apiService.getPartnerStudios(),
      ]);
      setAllStudios(studiosRes.data || []);
      setPartnerStudios(partnerRes.studios || []);

      // How many locations each brand has. Derived from the studio list we
      // already fetched — no extra request, and it is the only thing that
      // decides whether app-identity fields are editable here.
      const counts: Record<string, number> = {};
      for (const st of (studiosRes.data || []) as any[]) {
        const b = st?.brand ? String(st.brand?._id ?? st.brand) : null;
        if (b) counts[b] = (counts[b] ?? 0) + 1;
      }
      setBrandBranchCounts(counts);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    if (!selectedStudio) return;
    try {
      setIsGenerating(true);
      setError(null);
      const res = await apiService.generatePartnerApiKey(selectedStudio._id);
      setGeneratedApiKey(res.apiKey);
      setShowApiKey(true);
      setSuccess(`API key generated for ${selectedStudio.studioName}`);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to generate API key');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRevokeKey = async () => {
    if (!studioToRevoke) return;
    try {
      setIsRevoking(true);
      setError(null);
      await apiService.revokePartnerApiKey(studioToRevoke._id);
      setSuccess(`API key revoked for ${studioToRevoke.studioName}`);
      setRevokeDialogOpen(false);
      setStudioToRevoke(null);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to revoke API key');
    } finally {
      setIsRevoking(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbarMessage('Copied to clipboard');
    setSnackbarOpen(true);
  };

  const openConfigDialog = (studio: PartnerStudio) => {
    setConfigStudio(studio);
    setConfigForm(studio.whitelabelConfig || {});
    setShowRazorpaySecret(false);
    setConfigDialogOpen(true);
  };

  const handleSaveConfig = async () => {
    if (!configStudio) return;
    try {
      setIsSavingConfig(true);
      setError(null);
      // A branch never writes app identity from here, even though the fields
      // are on screen (read-only, for context). They are shared by every
      // location and are edited on the brand — and stripping them means this
      // save cannot resurrect a stale value that the brand has since changed.
      const BRAND_FIELDS = [
        'appSource', 'appName', 'senderName', 'logoUrl', 'primaryColor',
        'accentColor', 'websiteUrl', 'privacyPolicyUrl', 'termsUrl',
        'deepLinkScheme', 'playStoreUrl', 'appStoreUrl', 'linkBaseUrl',
      ];
      let payload: any = configForm;
      if (isBranchOfBrand(configStudio)) {
        payload = { ...configForm };
        BRAND_FIELDS.forEach((k) => delete payload[k]);
      }
      await apiService.updateStudio(configStudio._id, { whitelabelConfig: payload });
      setSuccess(`Branding config saved for ${configStudio.studioName}`);
      setConfigDialogOpen(false);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to save branding config');
    } finally {
      setIsSavingConfig(false);
    }
  };

  const partnerStudioIds = new Set(partnerStudios.map((s) => s._id));

  const nonPartnerStudios = allStudios.filter(
    (s) =>
      !partnerStudioIds.has(s._id) &&
      (s.studioName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h3" component="h1" fontWeight="bold" color={colors.textPrimary} gutterBottom>
            Whitelabel Partners
          </Typography>
          <Typography variant="body1" color={colors.textSecondary}>
            Manage API keys for studios using the partner/whitelabel API
          </Typography>
        </Box>
        <IconButton onClick={fetchData} sx={{ color: colors.textSecondary }}>
          <Refresh />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 4 }}>
        <Card sx={{ borderLeft: `4px solid ${colors.primary}`, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Total Studios
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={colors.primary}>
              {allStudios.length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderLeft: `4px solid ${colors.success}`, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Whitelabel Active
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={colors.success}>
              {partnerStudios.length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderLeft: `4px solid ${colors.info}`, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Available Studios
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={colors.info}>
              {allStudios.length - partnerStudios.length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Active Partners Section */}
      <Typography variant="h5" fontWeight="bold" mb={2} color={colors.textPrimary}>
        Active Whitelabel Partners
      </Typography>

      {partnerStudios.length === 0 ? (
        <Card sx={{ borderRadius: 2, p: 4, textAlign: 'center', mb: 4 }}>
          <VpnKey sx={{ fontSize: 48, color: colors.textSecondary, mb: 1 }} />
          <Typography variant="h6" color="textSecondary">
            No active whitelabel partners yet
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Generate an API key for a studio below to get started
          </Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2, mb: 4 }}>
          {partnerStudios.map((studio) => (
            <Card
              key={studio._id}
              sx={{
                borderRadius: 2,
                border: `1px solid ${colors.success}40`,
                transition: 'all 0.2s ease',
                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box display="flex" alignItems="center" gap={2} flex={1}>
                    <Avatar sx={{ bgcolor: colors.success, width: 48, height: 48 }}>
                      <VpnKey />
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        {studio.studioName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {studio.email}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" gap={1}>
                    <Chip
                      icon={<CheckCircle sx={{ fontSize: 16 }} />}
                      label="Active"
                      size="small"
                      sx={{ bgcolor: colors.success, color: '#fff', fontWeight: 500 }}
                    />
                  </Box>
                </Box>

                {studio.whitelabelAllowedOrigins?.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                      Allowed Origins:
                    </Typography>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {studio.whitelabelAllowedOrigins.map((origin, i) => (
                        <Chip key={i} label={origin} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Branding config summary */}
                {studio.whitelabelConfig?.appSource && (
                  <Box mt={1.5} p={1.5} sx={{ bgcolor: '#f0f7ff', borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary" display="block" mb={0.5} fontWeight={600}>
                      Branding Config
                    </Typography>
                    <Typography variant="caption" display="block" color="textSecondary">
                      <strong>App ID:</strong> {studio.whitelabelConfig.appSource}
                    </Typography>
                    {studio.whitelabelConfig.appName && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        <strong>Brand:</strong> {studio.whitelabelConfig.appName}
                      </Typography>
                    )}
                    {studio.whitelabelConfig.supportEmail && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        <strong>Support:</strong> {studio.whitelabelConfig.supportEmail}
                      </Typography>
                    )}
                    <Typography variant="caption" display="block" color={studio.whitelabelConfig.razorpayKeyId ? 'success.main' : 'text.secondary'}>
                      <strong>Razorpay:</strong> {studio.whitelabelConfig.razorpayKeyId ? `✓ ${studio.whitelabelConfig.razorpayKeyId}` : 'Using SweatSocial default'}
                    </Typography>
                  </Box>
                )}

                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} pt={2} borderTop={`1px solid ${colors.divider}`}>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Edit Branding Config">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Tune />}
                        onClick={() => openConfigDialog(studio)}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {studio.whitelabelConfig?.appSource ? 'Edit Config' : 'Set Config'}
                      </Button>
                    </Tooltip>
                    <Tooltip title="Regenerate API Key">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={() => {
                          const s = allStudios.find((as) => as._id === studio._id);
                          if (s) {
                            setSelectedStudio(s);
                            setGeneratedApiKey(null);
                            setShowApiKey(false);
                            setGenerateDialogOpen(true);
                          }
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        Regen Key
                      </Button>
                    </Tooltip>
                  </Box>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<Delete />}
                    onClick={() => {
                      setStudioToRevoke(studio);
                      setRevokeDialogOpen(true);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Revoke
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Enable Whitelabel Section */}
      <Typography variant="h5" fontWeight="bold" mb={2} color={colors.textPrimary}>
        Enable Whitelabel for a Studio
      </Typography>

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <TextField
            fullWidth
            placeholder="Search studios by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="medium"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: colors.textSecondary }} />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {nonPartnerStudios.length === 0 ? (
        <Card sx={{ borderRadius: 2, p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            {searchTerm ? 'No matching studios found' : 'All studios are already whitelabel partners'}
          </Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
          {nonPartnerStudios.map((studio) => (
            <Card
              key={studio._id}
              sx={{
                borderRadius: 2,
                border: `1px solid ${colors.divider}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  borderColor: colors.primary,
                },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar
                    src={resolveStudioLogoUrl(studio)}
                    sx={{ bgcolor: colors.primary, width: 44, height: 44 }}
                  >
                    {studio.studioName?.charAt(0)?.toUpperCase() || 'S'}
                  </Avatar>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body1" fontWeight="600" noWrap>
                      {studio.studioName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" noWrap>
                      {studio.email}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" gap={0.75} mb={2}>
                  <Chip
                    label={studio.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={studio.isActive ? 'success' : 'error'}
                    sx={{ fontWeight: 500, fontSize: '0.7rem', height: 24 }}
                  />
                  {studio.city && (
                    <Chip
                      label={studio.city}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 24 }}
                    />
                  )}
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<VpnKey />}
                  onClick={() => {
                    setSelectedStudio(studio);
                    setGeneratedApiKey(null);
                    setShowApiKey(false);
                    setGenerateDialogOpen(true);
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Generate API Key
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Generate API Key Dialog */}
      <Dialog
        open={generateDialogOpen}
        onClose={() => {
          if (!isGenerating) {
            setGenerateDialogOpen(false);
            setGeneratedApiKey(null);
            setShowApiKey(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <VpnKey /> Generate API Key
        </DialogTitle>
        <DialogContent>
          {!generatedApiKey ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                This will generate a new API key for <strong>{selectedStudio?.studioName}</strong> and enable whitelabel access.
                {partnerStudioIds.has(selectedStudio?._id || '') && (
                  <> The existing key will be replaced.</>
                )}
              </Alert>
              <Box display="flex" alignItems="center" gap={2} p={2} sx={{ bgcolor: colors.backgroundTertiary, borderRadius: 1 }}>
                <Avatar src={resolveStudioLogoUrl(selectedStudio)} sx={{ bgcolor: colors.primary }}>
                  {selectedStudio?.studioName?.charAt(0) || 'S'}
                </Avatar>
                <Box>
                  <Typography variant="body1" fontWeight="600">{selectedStudio?.studioName}</Typography>
                  <Typography variant="body2" color="textSecondary">{selectedStudio?.email}</Typography>
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Copy this API key now. It will not be shown again.
              </Alert>
              <Box
                sx={{
                  p: 2,
                  bgcolor: colors.primary,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    color: '#fff',
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    fontSize: '0.8rem',
                  }}
                >
                  {showApiKey ? generatedApiKey : '•'.repeat(40)}
                </Typography>
                <IconButton size="small" onClick={() => setShowApiKey(!showApiKey)} sx={{ color: '#fff' }}>
                  {showApiKey ? <VisibilityOff /> : <Visibility />}
                </IconButton>
                <IconButton size="small" onClick={() => copyToClipboard(generatedApiKey!)} sx={{ color: '#fff' }}>
                  <ContentCopy />
                </IconButton>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setGenerateDialogOpen(false);
              setGeneratedApiKey(null);
              setShowApiKey(false);
            }}
            variant="outlined"
            disabled={isGenerating}
          >
            {generatedApiKey ? 'Close' : 'Cancel'}
          </Button>
          {!generatedApiKey && (
            <Button
              onClick={handleGenerateKey}
              variant="contained"
              disabled={isGenerating}
              startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : <VpnKey />}
              sx={{ fontWeight: 600 }}
            >
              {isGenerating ? 'Generating...' : 'Generate Key'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Revoke Dialog */}
      <Dialog
        open={revokeDialogOpen}
        onClose={() => !isRevoking && setRevokeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: colors.error, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Cancel /> Revoke API Key
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will permanently revoke the API key for <strong>{studioToRevoke?.studioName}</strong>.
            Their partner application will immediately lose access.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevokeDialogOpen(false)} variant="outlined" disabled={isRevoking}>
            Cancel
          </Button>
          <Button
            onClick={handleRevokeKey}
            color="error"
            variant="contained"
            disabled={isRevoking}
            startIcon={isRevoking ? <CircularProgress size={16} color="inherit" /> : <Delete />}
            sx={{ fontWeight: 600 }}
          >
            {isRevoking ? 'Revoking...' : 'Revoke Key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Branding Config Dialog */}
      <Dialog
        open={configDialogOpen}
        onClose={() => !isSavingConfig && setConfigDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tune />{' '}
          {isBranchOfBrand(configStudio)
            ? `Location Config — ${configStudio?.studioName}`
            : `Branding Config — ${configStudio?.studioName}`}
        </DialogTitle>
        <DialogContent>
          {/* The page-level error sits behind this dialog; repeat it here so a
              failed save is visible instead of looking like nothing happened. */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            {isBranchOfBrand(configStudio)
              ? 'This is one location of a brand. Payments and support contacts are set here, per location. The app\'s identity and branding are shared by every location and are edited on the brand.'
              : 'These values control how emails and notifications appear to users of this whitelabel app.'}
          </Alert>

          <Typography variant="subtitle2" fontWeight={700} mb={1} color="textSecondary">
            APP IDENTITY
            {isBranchOfBrand(configStudio) && (
              <Typography component="span" variant="caption" sx={{ ml: 1, fontWeight: 500 }}>
                — shared by every location, edit on the brand
              </Typography>
            )}
          </Typography>
          <Box display="flex" flexDirection="column" gap={2} mb={2}>
            <TextField
              label="App Source ID"
              placeholder="e.g. purple_pilates"
              value={configForm.appSource || ''}
              onChange={(e) => setConfigForm({ ...configForm, appSource: e.target.value })}
              fullWidth
              size="small"
              helperText="Unique identifier — matches x-app-source header. Use snake_case."
              disabled={isBranchOfBrand(configStudio)}
            />
            <TextField
              label="App / Brand Name"
              placeholder="e.g. Purple Pilates"
              value={configForm.appName || ''}
              onChange={(e) => setConfigForm({ ...configForm, appName: e.target.value })}
              fullWidth
              size="small"
              helperText="Shown in emails as 'booked through [Brand Name]'"
              disabled={isBranchOfBrand(configStudio)}
            />
            <TextField
              label="Email Sender Name"
              placeholder="e.g. Purple Pilates"
              value={configForm.senderName || ''}
              onChange={(e) => setConfigForm({ ...configForm, senderName: e.target.value })}
              fullWidth
              size="small"
              helperText="Displayed as the From name in the inbox"
              disabled={isBranchOfBrand(configStudio)}
            />
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={700} mb={1} color="textSecondary">
            SUPPORT CONTACT
          </Typography>
          <Box display="flex" flexDirection="column" gap={2} mb={2}>
            <TextField
              label="Reply-To Email"
              placeholder="e.g. support@purplepilates.in"
              value={configForm.replyToEmail || ''}
              onChange={(e) => setConfigForm({ ...configForm, replyToEmail: e.target.value })}
              fullWidth
              size="small"
              helperText="Users who reply to emails are routed here"
            />
            <TextField
              label="Support Email"
              placeholder="e.g. support@purplepilates.in"
              value={configForm.supportEmail || ''}
              onChange={(e) => setConfigForm({ ...configForm, supportEmail: e.target.value })}
              fullWidth
              size="small"
              helperText="Shown in email footers"
            />
            <TextField
              label="Support Contact"
              placeholder="e.g. +91 9999999999"
              value={configForm.supportContact || ''}
              onChange={(e) => setConfigForm({ ...configForm, supportContact: e.target.value })}
              fullWidth
              size="small"
              helperText="Phone number shown in email footers"
            />
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={700} mb={1} color="textSecondary">
            LINKS (OPTIONAL)
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Website / App URL"
              placeholder="e.g. https://purplepilates.in"
              value={configForm.websiteUrl || ''}
              onChange={(e) => setConfigForm({ ...configForm, websiteUrl: e.target.value })}
              fullWidth
              size="small"
              helperText="Used as the 'Renew Now' link in pack expiry emails"
              disabled={isBranchOfBrand(configStudio)}
            />
            <TextField
              label="Deep Link Scheme"
              placeholder="e.g. purplepilates"
              value={configForm.deepLinkScheme || ''}
              onChange={(e) => setConfigForm({ ...configForm, deepLinkScheme: e.target.value })}
              fullWidth
              size="small"
              helperText="The app's custom URL scheme (no ://). Event share links open {scheme}://events/{id}"
              disabled={isBranchOfBrand(configStudio)}
            />
            <TextField
              label="Play Store URL"
              placeholder="https://play.google.com/store/apps/details?id=..."
              value={configForm.playStoreUrl || ''}
              onChange={(e) => setConfigForm({ ...configForm, playStoreUrl: e.target.value })}
              fullWidth
              size="small"
              helperText="Android fallback when the app isn't installed (event share links)"
              disabled={isBranchOfBrand(configStudio)}
            />
            <TextField
              label="App Store URL"
              placeholder="https://apps.apple.com/in/app/..."
              value={configForm.appStoreUrl || ''}
              onChange={(e) => setConfigForm({ ...configForm, appStoreUrl: e.target.value })}
              fullWidth
              size="small"
              helperText="iOS fallback when the app isn't installed (event share links)"
              disabled={isBranchOfBrand(configStudio)}
            />
            <TextField
              label="Custom Share Domain"
              placeholder="e.g. https://link.purplepilates.in"
              value={configForm.linkBaseUrl || ''}
              onChange={(e) => setConfigForm({ ...configForm, linkBaseUrl: e.target.value })}
              fullWidth
              size="small"
              helperText="Partner's own domain for share links (e.g. https://link.purplepilates.in/e/{id}). Leave blank to use the SweatSocial default. Requires the domain's DNS to point to our server."
              disabled={isBranchOfBrand(configStudio)}
            />
            <TextField
              label="Logo URL"
              placeholder="https://..."
              value={configForm.logoUrl || ''}
              onChange={(e) => setConfigForm({ ...configForm, logoUrl: e.target.value })}
              fullWidth
              size="small"
              disabled={isBranchOfBrand(configStudio)}
            />
            <TextField
              label="Privacy Policy URL"
              placeholder="https://purplepilates.in/privacy"
              value={configForm.privacyPolicyUrl || ''}
              onChange={(e) => setConfigForm({ ...configForm, privacyPolicyUrl: e.target.value })}
              fullWidth
              size="small"
              helperText="Leave blank to use SweatSocial's default privacy policy"
              disabled={isBranchOfBrand(configStudio)}
            />
            <TextField
              label="Terms & Conditions URL"
              placeholder="https://purplepilates.in/terms"
              value={configForm.termsUrl || ''}
              onChange={(e) => setConfigForm({ ...configForm, termsUrl: e.target.value })}
              fullWidth
              size="small"
              helperText="Leave blank to use SweatSocial's default terms"
              disabled={isBranchOfBrand(configStudio)}
            />
            <TextField
              label="WhatsApp Number"
              placeholder="919876543210"
              value={configForm.whatsappNumber || ''}
              onChange={(e) => setConfigForm({ ...configForm, whatsappNumber: e.target.value })}
              fullWidth
              size="small"
              helperText="Full number with country code, no + or spaces (e.g. 919876543210). Leave blank to use SweatSocial default."
            />
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={700} mb={0.5} color="textSecondary">
            RAZORPAY (OPTIONAL)
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            Leave blank to use SweatSocial's default Razorpay account. Set both fields to route payments into the studio's own Razorpay account.
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Razorpay Key ID (public)"
              placeholder="rzp_live_XXXXXX"
              value={configForm.razorpayKeyId || ''}
              onChange={(e) => setConfigForm({ ...configForm, razorpayKeyId: e.target.value })}
              fullWidth
              size="small"
              helperText="Must match the public key used in the studio's app"
            />
            <TextField
              label="Razorpay Key Secret"
              placeholder="••••••••••••••••"
              value={configForm.razorpayKeySecret || ''}
              onChange={(e) => setConfigForm({ ...configForm, razorpayKeySecret: e.target.value })}
              fullWidth
              size="small"
              type={showRazorpaySecret ? 'text' : 'password'}
              helperText="Stored securely — used server-side only"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowRazorpaySecret(p => !p)}>
                      {showRazorpaySecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={700} mb={0.5} color="textSecondary">
            INSTRUCTOR BEHAVIOUR
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={configForm.instructorViewAllBookings ?? false}
                onChange={(e) => setConfigForm({ ...configForm, instructorViewAllBookings: e.target.checked })}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>Instructors see all bookings</Typography>
                <Typography variant="caption" color="text.secondary">
                  When on, instructors in the partner app see every booking — not just their own assigned sessions. Requires App Source ID to be set so the admin panel filters correctly.
                </Typography>
              </Box>
            }
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={700} mb={0.5} color="textSecondary">
            STUDIO OWNER NOTIFICATIONS
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            Controls whether the studio owner receives an email when bookings are made or cancelled. User notifications are always sent regardless. Applies only to this whitelabel app — SweatSocial bookings always notify.
          </Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={configForm.ownerNotifPrefs?.onBookingConfirmed ?? true}
                  onChange={(e) => setConfigForm({
                    ...configForm,
                    ownerNotifPrefs: { ...configForm.ownerNotifPrefs, onBookingConfirmed: e.target.checked },
                  })}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Email on trial & drop-in booking</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Studio owner gets an email when a member pays for a class directly (trial or cash/online — not using a pack). Event bookings and pack purchases always notify regardless of this setting.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={configForm.ownerNotifPrefs?.onBookingCancelled ?? true}
                  onChange={(e) => setConfigForm({
                    ...configForm,
                    ownerNotifPrefs: { ...configForm.ownerNotifPrefs, onBookingCancelled: e.target.checked },
                  })}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Email on booking cancelled</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Studio owner gets an email each time a member cancels a booking.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={configForm.ownerNotifPrefs?.onPackBookingConfirmed ?? true}
                  onChange={(e) => setConfigForm({
                    ...configForm,
                    ownerNotifPrefs: { ...configForm.ownerNotifPrefs, onPackBookingConfirmed: e.target.checked },
                  })}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Email when member uses pack credits</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Studio owner gets an email each time a member books a class using their pack (deducts a credit). Turn off to reduce noise from high-volume pack bookings.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={configForm.ownerNotifPrefs?.preventEventCancellation ?? false}
                  onChange={(e) => setConfigForm({
                    ...configForm,
                    ownerNotifPrefs: { ...configForm.ownerNotifPrefs, preventEventCancellation: e.target.checked },
                  })}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Block event cancellations</Typography>
                  <Typography variant="caption" color="text.secondary">
                    When on, members cannot cancel their event bookings from the app.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={configForm.ownerNotifPrefs?.preventDropInCancellation ?? false}
                  onChange={(e) => setConfigForm({
                    ...configForm,
                    ownerNotifPrefs: { ...configForm.ownerNotifPrefs, preventDropInCancellation: e.target.checked },
                  })}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Block trial & drop-in cancellations</Typography>
                  <Typography variant="caption" color="text.secondary">
                    When on, members cannot cancel trial or cash/online-paid class bookings. Pack bookings remain cancellable (credit is restored to the pack).
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfigDialogOpen(false)}
            variant="outlined"
            disabled={isSavingConfig}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveConfig}
            variant="contained"
            disabled={isSavingConfig}
            startIcon={isSavingConfig ? <CircularProgress size={16} color="inherit" /> : <Tune />}
            sx={{ fontWeight: 600 }}
          >
            {isSavingConfig ? 'Saving...' : 'Save Config'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default WhitelabelPage;

